import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useSelector } from "react-redux";
import { axInst } from "../../Login/Service";
import TotalDealerRevenueData from "./TotalDealerRevenueData";

import { getMetricConfig } from "./formatUtils";

export const fetchServiceJobCardData = async (filterData) => {
  const { data } = await axInst.post(
    "collbax-service/collbax_service_job_card",
    filterData,
  );
  return data;
};

const DealerProfitablility = ({ hasOpened }) => {
  const { data, submitted } = useSelector(
    (state) => state.filtersServiceCollabax,
  );

  const {
    data: serviceJobCardData,
    isLoading: serviceJobCardLoading,
    isFetching: serviceJobCardFetching,
  } = useQuery({
    queryKey: ["serviceJobCardData", submitted],
    queryFn: () => fetchServiceJobCardData(data),
    enabled: hasOpened !== undefined ? hasOpened : true,
  });

  // console.log("serviceJobCardData", serviceJobCardData);

  if (hasOpened && (serviceJobCardLoading || serviceJobCardFetching)) {
    return (
      <div className="w-full h-60 flex flex-col items-center justify-center bg-white rounded-lg shadow-sm">
        <div className="w-12 h-12 border-4 border-blue-200 border-t-violet-600 rounded-full animate-spin mb-4"></div>
        <p className="text-gray-500 text-sm font-medium animate-pulse">
          Updating Profitability data...
        </p>
      </div>
    );
  }

  // Map serviceJobCardData to mainObject structure to reuse logic
  // The API response structure can vary:
  // 1. { "RSM_Name": { "FY-25": ..., "value": { ... } } } -> Raw RSM map
  // 2. { "Metric_Name": { "RSM_Name": { ... } } } -> Metric-wrapped map

  const serviceData = serviceJobCardData || {};
  const firstKey = Object.keys(serviceData)[0];
  const firstVal = firstKey ? serviceData[firstKey] : null;

  // Check if firstVal exists and looks like a data object (has monthly 'value' or 'YTD')
  // If it does, the top level is RSM names, and we should wrap it.
  const isRawRSMMap =
    firstVal &&
    (firstVal.value ||
      Object.keys(firstVal).some(
        (k) =>
          k.toUpperCase().startsWith("YTD") ||
          k.startsWith("FY-") ||
          k.startsWith("FY_"),
      ));

  const mainObject = isRawRSMMap ? { Total_Jobcard: serviceData } : serviceData;

  const metricKeys = Object.keys(mainObject);
  const rsmNames =
    metricKeys.length > 0 ? Object.keys(mainObject[metricKeys[0]]) : [];

  // Dynamically detect year and YTD keys
  let yearKey = "FY-25";
  let ytdKey = "YTD";

  if (metricKeys.length > 0 && rsmNames.length > 0) {
    const dataObj = mainObject[metricKeys[0]][rsmNames[0]] || {};
    const keys = Object.keys(dataObj).filter((k) => k !== "value");

    const possibleYear = keys.find(
      (k) => k.startsWith("FY-") || k.startsWith("FY_"),
    );
    const possibleYtd = keys.find((k) => k.toUpperCase().startsWith("YTD"));

    if (possibleYear) yearKey = possibleYear;
    if (possibleYtd) ytdKey = possibleYtd;

    // Fallback logic if specific patterns aren't matched
    if (!possibleYear && keys.length > 0)
      yearKey = keys.find((k) => k !== ytdKey) || keys[0];
    if (!possibleYtd && keys.length > 1)
      ytdKey = keys.find((k) => k !== yearKey) || keys[1];
  }

  // Group metric keys into pairs for the layout
  const metricGroups = [];
  for (let i = 0; i < metricKeys.length; i += 2) {
    metricGroups.push([metricKeys[i], metricKeys[i + 1]]);
  }

  const renderSection = (metric1Key, metric2Key) => {
    return (
      <div
        data-pdf-chart
        className="flex justify-start items-start w-full min-w-[800px] mb-8"
      >
        {/* RSM NAME COLUMN */}
        <div className="max-w-[150px] w-full flex flex-col items-center justify-center">
          <p className="w-full py-1 bg-[#334155] text-xs text-white rounded-tl-md flex items-center justify-center">
            RSM NAME
          </p>
          <div className="w-full mt-1 flex flex-col gap-1">
            {rsmNames.map((name, index) => (
              <div
                key={name}
                className={`w-full h-[100px] bg-white border border-x border-b border-gray-300 flex items-center justify-center uppercase ${
                  index === rsmNames.length - 1 ? "rounded-bl-md" : ""
                }`}
              >
                <p className="text-sm text-gray-700 font-medium">{name}</p>
              </div>
            ))}
          </div>
        </div>

        {/* METRIC 1 COLUMN */}
        {metric1Key && (
          <div className="max-w-[500px] w-full flex flex-col items-center justify-center mx-1">
            <p
              className={`w-full py-1 bg-[#2563eb] text-xs text-white font-medium flex items-center justify-center uppercase ${
                !metric2Key ? "rounded-tr-md" : ""
              }`}
            >
              {metric1Key.replace(/_/g, " ")}
            </p>
            <div className="w-full mt-1 flex flex-col gap-1">
              {rsmNames.map((name, index) => {
                const metricData = mainObject[metric1Key]?.[name];
                const yearV = metricData?.[yearKey];
                const ytdV = metricData?.[ytdKey];
                const isMetric1Percent = metric1Key?.includes("%");
                const config1 = getMetricConfig(
                  yearV,
                  ytdV,
                  metricData?.value,
                  isMetric1Percent,
                );

                return (
                  <div
                    key={name}
                    className={`w-full h-[100px] bg-white border border-x border-b border-gray-300 ${
                      !metric2Key && index === rsmNames.length - 1
                        ? "rounded-br-md"
                        : ""
                    }`}
                  >
                    <TotalDealerRevenueData
                      year={yearKey}
                      yearVal={config1.formatVal(yearV)}
                      ytd={ytdKey}
                      ytdVal={config1.formatVal(ytdV)}
                      graphData={metricData?.value}
                      divisor={config1.divisor}
                      suffix={config1.suffix}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* METRIC 2 COLUMN */}
        {metric2Key && (
          <div className="max-w-[500px] w-full flex flex-col items-center justify-center">
            <p className="w-full py-1 bg-[#7c3aed] text-xs text-white rounded-tr-md font-medium flex items-center justify-center uppercase">
              {metric2Key.replace(/_/g, " ")}
            </p>
            <div className="w-full mt-1 flex flex-col gap-1">
              {rsmNames.map((name, index) => {
                const metricData = mainObject[metric2Key]?.[name];
                const yearV = metricData?.[yearKey];
                const ytdV = metricData?.[ytdKey];
                const isMetric2Percent = metric2Key?.includes("%");
                const config2 = getMetricConfig(
                  yearV,
                  ytdV,
                  metricData?.value,
                  isMetric2Percent,
                );

                return (
                  <div
                    key={name}
                    className={`w-full h-[100px] bg-white border border-x border-b border-gray-300 ${
                      index === rsmNames.length - 1 ? "rounded-br-md" : ""
                    }`}
                  >
                    <TotalDealerRevenueData
                      year={yearKey}
                      yearVal={config2.formatVal(yearV)}
                      ytd={ytdKey}
                      ytdVal={config2.formatVal(ytdV)}
                      graphData={metricData?.value}
                      divisor={config2.divisor}
                      suffix={config2.suffix}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="block w-full max-w-full">
      {metricGroups.length > 0 ? (
        <div className="w-full max-w-full overflow-x-auto overflow-y-hidden touch-pan-x">
          <div className="w-full min-w-[800px]">
            {metricGroups.map((group, index) => (
              <div key={index}>{renderSection(group[0], group[1])}</div>
            ))}
          </div>
        </div>
      ) : (
        <div className="w-full py-10 flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500 font-medium">
            No data available for the selected filters
          </p>
        </div>
      )}
    </div>
  );
};

export default DealerProfitablility;
